import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { SocketContext } from "@contexts/SocketContext";
import SupplierContext from "@contexts/SupplierContext";
import UserContext from "@contexts/UserContext";
import EndpointsEnum from "@models/EndpointsEnum";
import ISerial from "@models/ISerial";
import IPO_Shipment_Catalog_Item_MapUI from "@models/IPO_Shipment_Catalog_Item_MapUI";
import IProcessPOShipmentItemSerialsDTO from "@models/DTO/IProcessPOShipmentItemSerialsDTO";
import IOnProcessPOShipmentItemSerialsDTO from "@models/DTO/IOnProcessPOShipmentItemSerialsDTO";
import IValidateSerialsDTO from "@models/DTO/IValidateSerialsDTO";
import IOnValidateSerialsDTO from "@models/DTO/IOnValidateSerialsDTO";
import UiContext from "@contexts/UiContext";
import NotificationLevelEnum from "@models/NotificationLevelEnum";
import SerialService from "@services/SerialService";

export interface IPendingSerial {
    value: string;
    isValid: boolean | null; // null = validating, true = valid, false = invalid
    error?: string;
}

export interface ISerialEntryItemState {
    serials: ISerial[];
    pendingSerials: IPendingSerial[];
    errors: string[];
    isSubmitting: boolean;
}

export interface ISerialEntryState {
    [catalogItemId: number]: ISerialEntryItemState;
}

const defaultItemState: ISerialEntryItemState = {
    serials: [],
    pendingSerials: [],
    errors: [],
    isSubmitting: false,
};

interface UsePurchaseOrderShipmentSerialsProps {
    poShipmentGuid: string;
}

export const usePurchaseOrderShipmentSerials = ({ poShipmentGuid }: UsePurchaseOrderShipmentSerialsProps) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const uiContext = useContext(UiContext);
    const socket = useContext(SocketContext)!;

    const [serialState, setSerialState] = useState<ISerialEntryState>({});
    const submitTimeoutsRef = useRef<Map<number, NodeJS.Timeout>>(new Map());

    const initializeSerials = useCallback((items: IPO_Shipment_Catalog_Item_MapUI[]) => {
        setSerialState((prev) => {
            const next: ISerialEntryState = { ...prev };
            let changed = false;

            for (const item of items) {
                const existing = prev[item.catalog_item_id];
                const serverSerials = item.Serials || [];
                if (existing || serverSerials.length === 0) continue;

                next[item.catalog_item_id] = {
                    ...defaultItemState,
                    serials: serverSerials,
                };
                changed = true;
            }

            return changed ? next : prev;
        });
    }, []);

    const getItemState = useCallback(
        (catalogItemId: number): ISerialEntryItemState => {
            return serialState[catalogItemId] || defaultItemState;
        },
        [serialState]
    );

    const addPendingSerial = useCallback(
        (catalogItemId: number, value: string) => {
            const trimmed = value.trim();
            if (!trimmed) return;

            setSerialState((prev) => {
                const itemState = prev[catalogItemId] || defaultItemState;

                // Check for local duplicates against pending and created serials
                const existingPending = itemState.pendingSerials.some((p) => p.value === trimmed);
                const existingCreated = itemState.serials.some((s) => s.value === trimmed);
                if (existingPending || existingCreated) return prev;

                return {
                    ...prev,
                    [catalogItemId]: {
                        ...itemState,
                        pendingSerials: [...itemState.pendingSerials, { value: trimmed, isValid: null }],
                    },
                };
            });

            // Emit validation request to server
            const payload: IValidateSerialsDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                serials: [trimmed],
            };
            socket.emit(EndpointsEnum.SerialValidateSerials, payload);
        },
        [userModel.guid, userModel.token, supplierModel.guid, socket]
    );

    const addBulkPendingSerials = useCallback((catalogItemId: number, values: string[]) => {
        setSerialState((prev) => {
            const itemState = prev[catalogItemId] || defaultItemState;
            const existingValues = new Set([...itemState.pendingSerials.map((p) => p.value), ...itemState.serials.map((s) => s.value)]);

            const newPending: IPendingSerial[] = values.filter((v) => !existingValues.has(v)).map((value) => ({ value, isValid: true }));

            if (newPending.length === 0) return prev;

            return {
                ...prev,
                [catalogItemId]: {
                    ...itemState,
                    pendingSerials: [...itemState.pendingSerials, ...newPending],
                },
            };
        });
    }, []);

    const removePendingSerial = useCallback((catalogItemId: number, index: number) => {
        setSerialState((prev) => {
            const itemState = prev[catalogItemId] || defaultItemState;
            return {
                ...prev,
                [catalogItemId]: {
                    ...itemState,
                    pendingSerials: itemState.pendingSerials.filter((_, i) => i !== index),
                },
            };
        });
    }, []);

    const submitSerials = useCallback(
        (catalogItemId: number, catalogItemMapId: number) => {
            const currentState = serialState[catalogItemId] || defaultItemState;
            const validSerials = currentState.pendingSerials.filter((p) => p.isValid === true);
            if (validSerials.length === 0 || currentState.isSubmitting) return;

            const payload: IProcessPOShipmentItemSerialsDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                poShipmentGuid,
                catalogItemId,
                catalogItemMapId,
                serials: validSerials.map((p) => p.value),
            };

            setSerialState((prev) => ({
                ...prev,
                [catalogItemId]: {
                    ...(prev[catalogItemId] || defaultItemState),
                    isSubmitting: true,
                    errors: [],
                },
            }));

            socket.emit(EndpointsEnum.PurchaseOrderProcessPOShipmentItemSerials, payload);

            // Fallback: reset isSubmitting if no server response within 10s
            const existingTimeout = submitTimeoutsRef.current.get(catalogItemId);
            if (existingTimeout) clearTimeout(existingTimeout);

            const timeoutId = setTimeout(() => {
                submitTimeoutsRef.current.delete(catalogItemId);
                setSerialState((prev) => {
                    const itemState = prev[catalogItemId];
                    if (itemState?.isSubmitting) {
                        return {
                            ...prev,
                            [catalogItemId]: {
                                ...itemState,
                                isSubmitting: false,
                                errors: [...itemState.errors, "Request timed out. Please try again."],
                            },
                        };
                    }
                    return prev;
                });
            }, 10000);
            submitTimeoutsRef.current.set(catalogItemId, timeoutId);
        },
        [serialState, userModel.guid, userModel.token, supplierModel.guid, poShipmentGuid, socket]
    );

    const deleteSerial = useCallback(
        async (catalogItemId: number, serialId: number) => {
            const itemState = serialState[catalogItemId] || defaultItemState;
            const serial = itemState.serials.find((s) => s.id === serialId);
            if (!serial) return;

            try {
                await SerialService.deleteSerial(serialId, serial.value, userModel, supplierModel);

                uiContext.showNotification({
                    message: `Serial ${serial.value} deleted`,
                    level: NotificationLevelEnum.success,
                });

                setSerialState((prev) => {
                    const currentItemState = prev[catalogItemId] || defaultItemState;
                    return {
                        ...prev,
                        [catalogItemId]: {
                            ...currentItemState,
                            serials: currentItemState.serials.filter((s) => s.id !== serialId),
                        },
                    };
                });
            } catch (error: any) {
                setSerialState((prev) => {
                    const currentItemState = prev[catalogItemId] || defaultItemState;
                    return {
                        ...prev,
                        [catalogItemId]: {
                            ...currentItemState,
                            errors: [...currentItemState.errors, error.message || "Failed to delete serial"],
                        },
                    };
                });
            }
        },
        [serialState, userModel, supplierModel, uiContext]
    );

    const clearErrors = useCallback((catalogItemId: number) => {
        setSerialState((prev) => {
            const itemState = prev[catalogItemId];
            if (!itemState) return prev;
            return {
                ...prev,
                [catalogItemId]: {
                    ...itemState,
                    errors: [],
                },
            };
        });
    }, []);

    useEffect(() => {
        const onProcessPOShipmentItemSerials = (data: IOnProcessPOShipmentItemSerialsDTO) => {
            const catalogItemId = data.catalogItemId;

            const existingTimeout = submitTimeoutsRef.current.get(catalogItemId);
            if (existingTimeout) {
                clearTimeout(existingTimeout);
                submitTimeoutsRef.current.delete(catalogItemId);
            }

            setSerialState((prev) => {
                const itemState = prev[catalogItemId] || defaultItemState;

                const createdValues = new Set(data.serials.map((s) => s.value));
                const remainingPending = itemState.pendingSerials.filter((p) => !createdValues.has(p.value));

                return {
                    ...prev,
                    [catalogItemId]: {
                        serials: [...itemState.serials, ...data.serials],
                        pendingSerials: remainingPending,
                        errors: data.errors || [],
                        isSubmitting: false,
                    },
                };
            });
        };

        const onValidateSerials = (data: IOnValidateSerialsDTO) => {
            setSerialState((prev) => {
                const next = { ...prev };
                let changed = false;

                for (const result of data.results) {
                    // Find which catalog item has this pending serial
                    for (const catalogItemId of Object.keys(next)) {
                        const id = Number(catalogItemId);
                        const itemState = next[id];
                        if (!itemState) continue;

                        const pendingIndex = itemState.pendingSerials.findIndex((p) => p.value === result.serial && p.isValid === null);

                        if (pendingIndex !== -1) {
                            const updatedPending = [...itemState.pendingSerials];
                            updatedPending[pendingIndex] = {
                                ...updatedPending[pendingIndex],
                                isValid: result.isValid,
                                error: result.error,
                            };
                            next[id] = {
                                ...itemState,
                                pendingSerials: updatedPending,
                            };
                            changed = true;
                            break;
                        }
                    }
                }

                return changed ? next : prev;
            });
        };

        socket.on(EndpointsEnum.PurchaseOrderOnProcessPOShipmentItemSerials, onProcessPOShipmentItemSerials);
        socket.on(EndpointsEnum.SerialOnValidateSerials, onValidateSerials);

        return () => {
            socket.off(EndpointsEnum.PurchaseOrderOnProcessPOShipmentItemSerials, onProcessPOShipmentItemSerials);
            socket.off(EndpointsEnum.SerialOnValidateSerials, onValidateSerials);
        };
    }, [socket]);

    useEffect(() => {
        const timeouts = submitTimeoutsRef.current;
        return () => {
            timeouts.forEach(clearTimeout);
            timeouts.clear();
        };
    }, []);

    return {
        serialState,
        initializeSerials,
        getItemState,
        addPendingSerial,
        addBulkPendingSerials,
        removePendingSerial,
        submitSerials,
        deleteSerial,
        clearErrors,
    };
};
